<template>
  <div class="gam-ratings">
    <div v-if="!props.isAuth" class="display-mode-row">
      <div class="display-mode-label">{{ localize('ratings.display.mode.label') }}</div>
      <div class="display-mode-spacer" />
      <div class="display-mode-chips">
        <GamChip
          :label="localize('ratings.display.mode.number')"
          :is-active="ratingStore.ratingDisplayMode === RatingDisplayMode.NUMBER"
          @click="ratingStore.setRatingDisplayMode(RatingDisplayMode.NUMBER)"
        />
        <GamChip
          :is-active="ratingStore.ratingDisplayMode === RatingDisplayMode.DOTS"
          @click="ratingStore.setRatingDisplayMode(RatingDisplayMode.DOTS)"
        >
          <span class="dot-preview">
            <span v-for="d in filledDots" :key="`f-${d}`" class="dot filled" />
            <span v-for="d in 3 - filledDots" :key="`e-${d}`" class="dot empty" />
          </span>
        </GamChip>
      </div>
    </div>

    <gam-rating-item
      v-for="rating in gamRating"
      :key="`rating-${rating.platform}`"
      ref="ratingItem"
      v-bind="rating"
      @change="toggleRating"
    />
  </div>
</template>

<script setup lang="ts">
import type { BaseUserInfo } from '@/core/data/user/user.interface';
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { gambits, localize } from '@/core/gambits';
import PlayerHelper from '@/core/helpers/player.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useRatingStore } from '@/stores/rating.store';
import GamChip from '@/views/components/GamChip.vue';
import GamRatingItem from '@/views/components/gam-ratings/GamRatingItem.vue';
import useLichess from '@/views/composables/auth/social/useLichess';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { GamRatingPlatform, ratingItems } from '@/views/composables/constants/main/ratings.constants';
import type { GamRatingItemType, GamRatingsType } from '@/views/composables/models/components/GamRatings';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

const props = defineProps<GamRatingsType>();
const router = useRouter();
const route = useRoute();
const ratingStore = useRatingStore();
const { ratings } = storeToRefs(ratingStore);
const { lichessGetRating, parseLichessRatings } = useLichess();
const emits = defineEmits([GamComponentsEmits.CHANGE, GamComponentsEmits.INITIALIZED]);
const ratingItem = ref<(typeof GamRatingItem)[]>();
const authUser = ref<BaseUserInfo>();
const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const filledDots = computed(() => {
  const r = Number(ratings.value.rating);
  return r > 0 ? PlayerHelper.ratingToDots(r) : 0;
});

onBeforeMount(async () => {
  await setConnected();
  gambits.ratingService.removeConnect();

  if (
    props.isAuth &&
    (ratings.value.lichessComBlitzRating ||
      ratings.value.lichessComBulletRating ||
      ratings.value.lichessComClassicalRating ||
      ratings.value.lichessComRapidRating)
  ) {
    toggleRating(GamRatingPlatform.LICHESS, true);
  }

  emits(GamComponentsEmits.INITIALIZED);
});

onBeforeUnmount(async () => {
  gambits.ratingService.removeConnect();
});

const getAuthUser = async (): Promise<void> => {
  const result = await gambits.userService.getUserInfo();

  if (result.data) {
    authUser.value = result.data;
    setLichessRatings(result.data);
  }
};

const getConnectCallback = (platform: GamRatingPlatform): void => {
  switch (platform) {
    case GamRatingPlatform.LICHESS:
      gambits.ratingService.setConnect(platform);
      lichessGetRating(gambits.configService.getServiceUrl(), props.isAuth);
      break;
    default:
      break;
  }
};

const setConnected = async () => {
  const lichessRatings = parseLichessRatings(route);

  if (gambits.ratingService.getConnect() === GamRatingPlatform.LICHESS && !lichessRatings) {
    useToast().info('At least 5 rated games from Lichess required.');
    userDetails.value && setOtherRatings(userDetails.value);

    return;
  }

  if (lichessRatings) {
    ratingStore.setLichessRatings(lichessRatings);
    if (userDetails.value) setOtherRatings(userDetails.value);
    if (props.isAuth) toggleRating(GamRatingPlatform.LICHESS, true);

    const result = await gambits.ratingService.updateRatings({
      username: userDetails.value!.username,
      firstName: userDetails.value!.firstName || '',
      lastName: userDetails.value!.lastName || '',
      birthDate: userDetails.value!.birthDate,
      ...ratings.value,
    });

    if (result.data) {
      router.replace({ ...route, query: { ...route.query, lichessComBulletRating: undefined } });
      useToast().info('Saved Lichess ratings.');
    }

    return;
  }

  if (props.isAuth) {
    await getAuthUser();

    return;
  }

  if (userDetails.value) {
    setLichessRatings(userDetails.value);
    setOtherRatings(userDetails.value);

    return;
  }
};

const setLichessRatings = (user: BaseUserInfo) => {
  ratingStore.setLichessRatings({
    title: user.title || undefined,
    fideRating: user.fideRating || undefined,
    lichessComBlitzRating: user.lichessComBlitzRating || undefined,
    lichessComRapidRating: user.lichessComRapidRating || undefined,
    lichessComBulletRating: user.lichessComBulletRating || undefined,
    lichessComClassicalRating: user.lichessComClassicalRating || undefined,
  });
};

const setOtherRatings = (user: BaseUserInfo) => {
  ratingStore.setByRatingKey('rating', user.rating);
  ratingStore.setByRatingKey('chessComBlitzRating', user.chessComBlitzRating);
  ratingStore.setByRatingKey('chessComRapidRating', user.chessComRapidRating);
  ratingStore.setByRatingKey('chessComBulletRating', user.chessComBulletRating);
  ratingStore.setByRatingKey('chessComClassicalRating', user.chessComClassicalRating);
  ratingStore.setRatingDisplayMode(user.ratingDisplayMode || RatingDisplayMode.NUMBER);
};

const gamRating = ref<GamRatingItemType[]>(ratingItems(getConnectCallback, props.isAuth));

const toggleRating = (platform: GamRatingPlatform, isOpen?: boolean) => {
  gamRating.value?.forEach((rating) => {
    if (rating.platform === platform) {
      rating.isOpen = isOpen || !rating.isOpen;
      nextTick(() => {
        ratingItem.value?.find((item) => item.getPlatform() === platform)?.updateChildHeight();
      });
    } else {
      rating.isOpen = false;
    }
  });
  emits(GamComponentsEmits.CHANGE, platform);
};
</script>

<style scoped lang="scss">
.gam-ratings {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: var(--spacing-l);
}

.display-mode-row {
  display: flex;
  gap: var(--spacing-md);
  flex-direction: row;
  align-items: center;
  padding-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--color-white-10);
}

.display-mode-label {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.05em;
  color: var(--color-white-50);
}

.display-mode-spacer {
  flex: 1;
}

.display-mode-chips {
  display: flex;
  gap: var(--spacing-xs);
}

.dot-preview {
  display: inline-flex;
  gap: 4px;
  align-items: center;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 1px;

  &.filled {
    background: currentColor;
  }

  &.empty {
    border: 1.5px solid currentColor;
    opacity: 0.4;
    background: transparent;
  }
}
</style>
